import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from '../components/ui';
import {
  CHEERLEAD_MESSAGE_MAX,
  PARTNER_NAME_MAX,
  PartnerViewRecord,
} from '../types/partner';
import { getPartnerView, sendCheerleadNote } from '../services/partnerService';
import MelioSeal from '../components/marketing/MelioSeal';
import OliveBranch from '../components/marketing/OliveBranch';

/**
 * Public partner-facing page. Reached via a capability link shared by the
 * member. Shows ONLY the shareable projection — streak, longest streak,
 * badge count — and a short note-back form. Styled in the Garden House
 * register: bone surface, everyday seal, hairline rules, underline inputs.
 */
export default function PartnerCheerPage() {
  const { linkId } = useParams<{ linkId: string }>();
  const [view, setView] = useState<PartnerViewRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [partnerName, setPartnerName] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem('melio.partnerName') || '';
  });
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!linkId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const v = await getPartnerView(linkId);
        if (!active) return;
        if (!v) setNotFound(true);
        else setView(v);
      } catch (err) {
        console.error('Partner view load failed', err);
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [linkId]);

  const handleSend = async () => {
    if (!view || !linkId) return;
    const trimmedName = partnerName.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName) {
      setError('Add your name so they know who the note is from.');
      return;
    }
    if (!trimmedMessage) {
      setError('Write a short note.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await sendCheerleadNote({
        linkId,
        memberUid: view.memberUid,
        partnerName: trimmedName,
        message: trimmedMessage,
      });
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('melio.partnerName', trimmedName);
      }
      setSent(true);
      setMessage('');
    } catch (err: any) {
      console.error('Send cheerlead failed', err);
      setError(err?.message || 'Could not send. Try again.');
    } finally {
      setSending(false);
    }
  };

  const remaining = useMemo(
    () => Math.max(0, CHEERLEAD_MESSAGE_MAX - message.length),
    [message],
  );

  if (loading) {
    return (
      <CheerShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner size="lg" />
        </div>
      </CheerShell>
    );
  }

  if (notFound || !view) {
    return (
      <CheerShell>
        <NotFoundCard />
      </CheerShell>
    );
  }

  if (view.status === 'revoked') {
    return (
      <CheerShell>
        <RevokedCard firstName={view.memberFirstName} />
      </CheerShell>
    );
  }

  return (
    <CheerShell>
      <div className="mx-auto max-w-xl">
        {/* Heading */}
        <header className="mb-10 text-center">
          <p className="gh-eyebrow mb-4">A note for</p>
          <h1
            className="gh-serif"
            style={{
              fontSize: 'clamp(2rem, 5vw, 2.75rem)',
              fontWeight: 300,
              lineHeight: 1.1,
              color: 'var(--gh-graphite)',
              margin: 0,
            }}
          >
            <em style={{ fontStyle: 'italic' }}>{view.memberFirstName}</em>
          </h1>
          <p
            className="gh-serif mx-auto mt-5 max-w-md"
            style={{
              fontStyle: 'italic',
              fontSize: '1rem',
              lineHeight: 1.55,
              color: 'var(--gh-ash)',
            }}
          >
            They&rsquo;re building a daily health habit. You can see how they&rsquo;re
            doing — and send a short note. No details, no numbers, just encouragement.
          </p>
          <div className="mt-8 flex justify-center">
            <OliveBranch width={72} color="var(--gh-oxblood)" />
          </div>
        </header>

        {/* Streak row */}
        <div
          className="grid grid-cols-3 gap-4 py-8 text-center"
          style={{
            borderTop: '1px solid var(--gh-rule)',
            borderBottom: '1px solid var(--gh-rule)',
          }}
        >
          <StreakCell
            label="Current streak"
            primary={view.currentStreak > 0 ? `Day ${view.currentStreak}` : '—'}
            secondary={view.currentStreak > 0 ? 'in a row' : 'no streak yet'}
          />
          <StreakCell
            label="Longest"
            primary={view.longestStreak > 0 ? `${view.longestStreak}` : '—'}
            secondary={view.longestStreak > 0 ? 'days' : ''}
          />
          <StreakCell
            label="Badges earned"
            primary={`${view.badgesEarnedCount}`}
            secondary={view.badgesEarnedCount === 1 ? 'badge' : 'badges'}
          />
        </div>

        {/* Note form / confirmation */}
        {sent ? (
          <div className="mt-12 text-center">
            <p
              className="gh-serif"
              style={{
                fontSize: '1.625rem',
                fontWeight: 300,
                fontStyle: 'italic',
                color: 'var(--gh-graphite)',
                margin: 0,
                marginBottom: 10,
              }}
            >
              Your note is on its way.
            </p>
            <p
              style={{
                fontSize: '0.9375rem',
                color: 'var(--gh-ash)',
                lineHeight: 1.55,
                maxWidth: 420,
                margin: '0 auto',
              }}
            >
              {view.memberFirstName} will see it next time they open Daily Bite.
            </p>
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setSent(false)}
                className="gh-btn-secondary"
                style={{ minWidth: 200 }}
              >
                Send another
              </button>
            </div>
            <div className="mt-12 flex justify-center">
              <OliveBranch width={56} color="var(--gh-oxblood)" />
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="mt-12"
          >
            <p className="gh-eyebrow-ash mb-2">Send a note</p>
            <h2
              className="gh-serif"
              style={{
                fontSize: '1.75rem',
                fontWeight: 300,
                letterSpacing: '-0.01em',
                color: 'var(--gh-graphite)',
                margin: 0,
                marginBottom: 28,
              }}
            >
              A short line goes a long way.
            </h2>

            <div style={{ display: 'grid', rowGap: 26 }}>
              <div>
                <label className="gh-input-label" htmlFor="partnerName">
                  Your name
                </label>
                <input
                  id="partnerName"
                  type="text"
                  className="gh-input"
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  placeholder="What should we call you?"
                  maxLength={PARTNER_NAME_MAX}
                />
              </div>

              <div>
                <label className="gh-input-label" htmlFor="cheerMessage">
                  Message
                </label>
                <textarea
                  id="cheerMessage"
                  className="gh-input"
                  value={message}
                  onChange={(e) =>
                    setMessage(e.target.value.slice(0, CHEERLEAD_MESSAGE_MAX))
                  }
                  placeholder="Proud of you for sticking with it this week."
                  rows={4}
                />
                <p
                  className="mt-1 text-right"
                  style={{
                    fontSize: '0.6875rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--gh-ash)',
                  }}
                >
                  {remaining} characters left
                </p>
              </div>
            </div>

            {error && (
              <p
                className="mt-4 text-center"
                style={{ fontSize: '0.875rem', color: 'var(--gh-oxblood)' }}
              >
                {error}
              </p>
            )}

            <div className="mt-10 flex justify-center">
              <button
                type="submit"
                disabled={sending}
                className="gh-btn-primary"
                style={{
                  minWidth: 240,
                  opacity: sending ? 0.6 : 1,
                  cursor: sending ? 'not-allowed' : 'pointer',
                }}
              >
                {sending ? 'Sending…' : `Send to ${view.memberFirstName}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </CheerShell>
  );
}

function CheerShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="gh-surface min-h-screen"
      style={{ backgroundColor: 'var(--gh-bone)', color: 'var(--gh-graphite)' }}
    >
      <header
        className="px-6 py-8 md:px-10"
        style={{ borderBottom: '1px solid var(--gh-rule)' }}
      >
        <div className="mx-auto flex max-w-xl items-center justify-center">
          <Link to="/" className="inline-flex">
            <MelioSeal size={28} withWordmark wordmarkSize={22} />
          </Link>
        </div>
      </header>

      <main className="px-6 py-14 md:px-10 md:py-20">{children}</main>

      <footer
        className="px-6 py-10 text-center md:px-10"
        style={{ borderTop: '1px solid var(--gh-rule)' }}
      >
        <p
          className="gh-serif"
          style={{
            fontStyle: 'italic',
            fontSize: '0.8125rem',
            color: 'var(--gh-ash)',
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          Melio · Private by default · No tracker data is ever shared with partners.
        </p>
      </footer>
    </div>
  );
}

function StreakCell({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary: string;
}) {
  return (
    <div>
      <p className="gh-eyebrow-ash mb-2">{label}</p>
      <p
        className="gh-serif"
        style={{
          fontSize: '1.75rem',
          fontWeight: 300,
          fontStyle: 'italic',
          color: 'var(--gh-oxblood)',
          lineHeight: 1.1,
          margin: 0,
          marginBottom: 4,
        }}
      >
        {primary}
      </p>
      <p style={{ fontSize: '0.75rem', color: 'var(--gh-ash)' }}>{secondary}</p>
    </div>
  );
}

function NotFoundCard() {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1
        className="gh-serif"
        style={{
          fontSize: '1.875rem',
          fontWeight: 300,
          color: 'var(--gh-graphite)',
          margin: 0,
          marginBottom: 14,
        }}
      >
        <em style={{ fontStyle: 'italic' }}>This link isn&rsquo;t valid.</em>
      </h1>
      <p
        style={{
          fontSize: '0.9375rem',
          color: 'var(--gh-ash)',
          lineHeight: 1.6,
          maxWidth: 360,
          margin: '0 auto',
        }}
      >
        Ask the person who sent it to share a fresh one from their Daily Bite
        settings.
      </p>
      <div className="mt-10 flex justify-center">
        <OliveBranch width={56} color="var(--gh-oxblood)" />
      </div>
    </div>
  );
}

function RevokedCard({ firstName }: { firstName: string }) {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1
        className="gh-serif"
        style={{
          fontSize: '1.875rem',
          fontWeight: 300,
          color: 'var(--gh-graphite)',
          margin: 0,
          marginBottom: 14,
        }}
      >
        <em style={{ fontStyle: 'italic' }}>{firstName} has paused this link.</em>
      </h1>
      <p
        style={{
          fontSize: '0.9375rem',
          color: 'var(--gh-ash)',
          lineHeight: 1.6,
          maxWidth: 360,
          margin: '0 auto',
        }}
      >
        No worries — they can send you a fresh one any time.
      </p>
      <div className="mt-10 flex justify-center">
        <OliveBranch width={56} color="var(--gh-oxblood)" />
      </div>
    </div>
  );
}
